<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <div>
        <h4 class="m-0 text-primary"><i class="bi bi-shop-window me-2"></i>Online Storefront</h4>
        <small class="text-muted">Design how your online shop looks: layout, colors, banners and product sections.</small>
      </div>
      <div class="d-flex gap-2 align-items-center flex-wrap">
        <select v-if="onlineStores.length" v-model="previewStoreId" class="form-select form-select-sm w-auto">
          <option v-for="store in onlineStores" :key="store.id" :value="store.id">{{ store.online_name || store.name }}</option>
        </select>
        <a v-if="previewUrl" :href="previewUrl" target="_blank" rel="noopener" class="btn btn-outline-primary btn-sm">
          <i class="bi bi-box-arrow-up-right me-1"></i>Preview shop
        </a>
        <span v-else class="small text-muted" title="Set a store online from the store details page first">
          <i class="bi bi-info-circle me-1"></i>No online store yet
        </span>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <Spinner spinner-class="mb-1" />
      <p class="text-muted mt-2">Loading storefront...</p>
    </div>

    <template v-else>
      <ul class="nav nav-pills mb-4 flex-wrap gap-1">
        <li class="nav-item" v-for="tab in tabs" :key="tab.key">
          <button type="button" class="nav-link" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
            <i class="bi me-1" :class="tab.icon"></i>{{ tab.label }}
          </button>
        </li>
      </ul>

      <!-- SECTIONS -->
      <div v-if="activeTab === 'sections'" class="row g-4">
        <div class="col-lg-8">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="m-0">Home page sections <span class="badge bg-secondary">{{ sections.length }}</span></h6>
            <small class="text-muted"><i class="bi bi-arrows-move me-1"></i>Drag to reorder</small>
          </div>

          <div v-if="!sections.length" class="alert alert-info">
            <h6 class="alert-heading"><i class="bi bi-magic me-1"></i>Your shop is using the default layout</h6>
            <p class="mb-2 small">
              Customers currently see a standard home page (hero banner, categories and product rows). Add widgets from the
              library to design your own, or start from a ready-made layout and adjust it.
            </p>
            <button v-if="canEdit" class="btn btn-sm btn-primary" :disabled="busy" @click="createStarterLayout">
              <span v-if="busy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-lightning-charge me-1"></i>Start with a ready-made layout
            </button>
          </div>

          <div class="section-list">
            <div v-for="(section, index) in sections" :key="section.id"
                 class="card section-card mb-2 border"
                 :class="{ 'section-inactive': !section.is_active, 'drag-over': dragOverIndex === index }"
                 :draggable="canEdit"
                 @dragstart="onDragStart(index, $event)"
                 @dragover.prevent="dragOverIndex = index"
                 @dragleave="dragOverIndex = null"
                 @drop.prevent="onDrop(index)"
                 @dragend="dragIndex = null; dragOverIndex = null">
              <div class="card-body py-2 px-3 d-flex align-items-center gap-3">
                <i v-if="canEdit" class="bi bi-grip-vertical text-muted drag-handle"></i>
                <div class="section-icon rounded bg-primary-subtle text-primary d-flex align-items-center justify-content-center">
                  <i class="bi" :class="sectionMeta(section).icon"></i>
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="fw-semibold text-truncate">
                    {{ section.title || sectionMeta(section).label }}
                  </div>
                  <div class="small text-muted d-flex flex-wrap gap-2 align-items-center">
                    <span>{{ sectionMeta(section).label }}</span>
                    <span v-if="sectionSummary(section)">· {{ sectionSummary(section) }}</span>
                    <span v-if="section.store" class="badge text-bg-light"><i class="bi bi-shop"></i> {{ section.store.online_name || section.store.name }}</span>
                    <span v-if="section.starts_at || section.ends_at" class="badge text-bg-warning"><i class="bi bi-calendar-event"></i> Scheduled</span>
                    <span v-if="needsItems(section)" class="badge text-bg-danger"><i class="bi bi-exclamation-triangle"></i> Add images to show</span>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-1 flex-shrink-0">
                  <button class="btn btn-sm btn-link text-secondary px-1" :disabled="!canEdit || index === 0" title="Move up" @click="move(index, -1)">
                    <i class="bi bi-arrow-up"></i>
                  </button>
                  <button class="btn btn-sm btn-link text-secondary px-1" :disabled="!canEdit || index === sections.length - 1" title="Move down" @click="move(index, 1)">
                    <i class="bi bi-arrow-down"></i>
                  </button>
                  <div class="form-check form-switch m-0 mx-1" :title="section.is_active ? 'Visible' : 'Hidden'">
                    <input type="checkbox" class="form-check-input" :checked="section.is_active" :disabled="!canEdit" @change="toggleSection(section)">
                  </div>
                  <button v-if="canEdit" class="btn btn-sm btn-outline-primary" @click="openEditor(section)">
                    <i class="bi bi-pencil"></i><span class="d-none d-md-inline ms-1">Edit</span>
                  </button>
                  <button v-if="canEdit" class="btn btn-sm btn-outline-secondary" title="Duplicate" @click="duplicate(section)">
                    <i class="bi bi-copy"></i>
                  </button>
                  <button v-if="canEdit" class="btn btn-sm btn-outline-danger" title="Delete" @click="removeSection(section)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4" v-if="canEdit">
          <div class="card border-0 bg-light sticky-lg-top widget-library">
            <div class="card-body">
              <h6 class="mb-1"><i class="bi bi-plus-square me-1"></i>Widget library</h6>
              <p class="small text-muted">Click a widget to add it to the bottom of your home page.</p>
              <button v-for="(meta, type) in sectionTypes" :key="type" type="button" :disabled="busy"
                      class="widget-option btn btn-light border w-100 text-start mb-2 d-flex gap-2 align-items-start"
                      @click="addSection(type)">
                <i class="bi fs-5 text-primary" :class="meta.icon"></i>
                <span>
                  <span class="fw-semibold d-block">{{ meta.label }}</span>
                  <span class="small text-muted">{{ meta.description }}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- THEME -->
      <div v-if="activeTab === 'theme'" class="row g-4">
        <div class="col-lg-6">
          <h6>Color presets</h6>
          <div class="d-flex flex-wrap gap-2 mb-4">
            <button v-for="preset in themePresets" :key="preset.name" type="button" class="btn btn-sm btn-outline-secondary preset-btn"
                    :disabled="!canEdit" @click="applyPreset(preset)">
              <span class="preset-swatches">
                <span :style="{ background: preset.theme.primary_color }"></span>
                <span :style="{ background: preset.theme.secondary_color }"></span>
                <span :style="{ background: preset.theme.header_bg }"></span>
                <span :style="{ background: preset.theme.nav_bg }"></span>
              </span>
              {{ preset.name }}
            </button>
          </div>

          <div v-for="group in colorGroups" :key="group.title" class="mb-3">
            <h6 class="text-muted small text-uppercase">{{ group.title }}</h6>
            <div class="row g-2">
              <div v-for="(label, key) in group.colors" :key="key" class="col-sm-6">
                <label class="form-label small mb-1">{{ label }}</label>
                <ColorInput v-model="settings.theme[key]" />
              </div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-sm-6">
              <label class="form-label fw-bold">Font</label>
              <select v-model="settings.theme.font_family" class="form-select">
                <option v-for="font in options.fonts" :key="font" :value="font">{{ font }}</option>
              </select>
            </div>
            <div class="col-sm-6">
              <label class="form-label fw-bold">Corner style</label>
              <select v-model="settings.theme.border_radius" class="form-select">
                <option value="none">Square</option>
                <option value="sm">Slightly rounded</option>
                <option value="md">Rounded</option>
                <option value="lg">More rounded</option>
                <option value="xl">Very rounded</option>
              </select>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="sticky-lg-top preview-sticky">
            <h6>Live preview</h6>
            <ThemePreview :settings="settings" :store-name="previewStoreName" />
            <small class="text-muted d-block mt-2">A simplified preview. Use "Preview shop" to see the real store.</small>
          </div>
        </div>
      </div>

      <!-- LAYOUT -->
      <div v-if="activeTab === 'layout'" class="row g-4">
        <div class="col-lg-6">
          <div class="row g-3">
            <div class="col-sm-6">
              <label class="form-label fw-bold">Header style</label>
              <select v-model="settings.layout.header_style" class="form-select">
                <option value="standard">Standard (logo, search, icons in one row)</option>
                <option value="centered">Centered logo</option>
                <option value="compact">Compact</option>
              </select>
            </div>
            <div class="col-sm-6">
              <label class="form-label fw-bold">Page width</label>
              <select v-model="settings.layout.container_width" class="form-select">
                <option value="standard">Standard (10 of 12 columns)</option>
                <option value="wide">Wide (11 of 12 columns)</option>
                <option value="full">Full width</option>
              </select>
            </div>
            <div class="col-sm-6">
              <label class="form-label fw-bold">Product card style</label>
              <select v-model="settings.layout.product_card_style" class="form-select">
                <option value="standard">Standard</option>
                <option value="bordered">Bordered</option>
                <option value="elevated">Elevated (shadow)</option>
                <option value="minimal">Minimal</option>
              </select>
            </div>
            <div class="col-sm-6">
              <label class="form-label fw-bold">Products per row (desktop)</label>
              <select v-model.number="settings.layout.products_per_row" class="form-select">
                <option v-for="n in [3,4,5,6]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
            <div class="col-12">
              <div class="form-check form-switch mb-2" v-for="(label, key) in layoutToggles" :key="key">
                <input :id="'layout-' + key" v-model="settings.layout[key]" type="checkbox" class="form-check-input">
                <label :for="'layout-' + key" class="form-check-label">{{ label }}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="sticky-lg-top preview-sticky">
            <h6>Live preview</h6>
            <ThemePreview :settings="settings" :store-name="previewStoreName" />
          </div>
        </div>
      </div>

      <!-- HEADER & FOOTER -->
      <div v-if="activeTab === 'header_footer'" class="row g-4">
        <div class="col-lg-6">
          <h6><i class="bi bi-window me-1"></i>Header</h6>
          <div class="form-check form-switch mb-2">
            <input id="h-ann" v-model="settings.header.announcement_enabled" type="checkbox" class="form-check-input">
            <label for="h-ann" class="form-check-label">Show announcement bar</label>
          </div>
          <div class="row g-2 mb-3" v-if="settings.header.announcement_enabled">
            <div class="col-md-7">
              <input v-model="settings.header.announcement_text" type="text" class="form-control" maxlength="200" placeholder="e.g. Free delivery on orders above KES 2,000">
            </div>
            <div class="col-md-5">
              <input v-model="settings.header.announcement_link" type="text" class="form-control" maxlength="255" placeholder="Link (optional), e.g. /shop?...">
            </div>
          </div>
          <label class="form-label fw-bold">Search box placeholder</label>
          <input v-model="settings.header.search_placeholder" type="text" class="form-control mb-3" maxlength="120">
          <div class="form-check form-switch mb-4">
            <input id="h-addr" v-model="settings.header.show_delivery_address" type="checkbox" class="form-check-input">
            <label for="h-addr" class="form-check-label">Show the customer's delivery address in the top-right of the header</label>
          </div>

          <h6><i class="bi bi-layout-text-window-reverse me-1"></i>Footer</h6>
          <label class="form-label fw-bold">About text</label>
          <textarea v-model="settings.footer.about_text" class="form-control mb-2" rows="3" maxlength="1000"></textarea>
          <div class="row g-2 mb-2">
            <div class="col-md-6">
              <label class="form-label small fw-bold">Contact email</label>
              <input v-model="settings.footer.contact_email" type="email" class="form-control" maxlength="120">
            </div>
            <div class="col-md-6">
              <label class="form-label small fw-bold">Contact phone</label>
              <input v-model="settings.footer.contact_phone" type="text" class="form-control" maxlength="30">
            </div>
          </div>
          <div class="form-check form-switch mb-2">
            <input id="f-hours" v-model="settings.footer.show_operating_hours" type="checkbox" class="form-check-input">
            <label for="f-hours" class="form-check-label">Show store operating hours</label>
          </div>
          <label class="form-label fw-bold">Copyright text</label>
          <input v-model="settings.footer.copyright_text" type="text" class="form-control" maxlength="200" :placeholder="`© ${new Date().getFullYear()} ${previewStoreName}. All rights reserved.`">
        </div>
        <div class="col-lg-6">
          <h6><i class="bi bi-share me-1"></i>Social media links</h6>
          <div class="row g-2 mb-4">
            <div v-for="(network, key) in socialNetworks" :key="key" class="col-md-6">
              <div class="input-group input-group-sm">
                <span class="input-group-text" :title="network.label"><i class="bi" :class="network.icon"></i></span>
                <input v-model="settings.footer.social[key]" type="text" class="form-control" :placeholder="network.label + ' URL'" maxlength="255">
              </div>
            </div>
          </div>
          <h6 class="d-flex justify-content-between align-items-center">
            <span><i class="bi bi-link-45deg me-1"></i>Footer links</span>
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="settings.footer.links.length >= 12"
                    @click="settings.footer.links.push({ label: '', url: '' })">
              <i class="bi bi-plus-lg"></i> Add link
            </button>
          </h6>
          <div v-for="(link, index) in settings.footer.links" :key="index" class="input-group input-group-sm mb-2">
            <input v-model="link.label" type="text" class="form-control" placeholder="Label" maxlength="50">
            <input v-model="link.url" type="text" class="form-control" placeholder="https://... or /path" maxlength="255">
            <button type="button" class="btn btn-outline-danger" @click="settings.footer.links.splice(index, 1)"><i class="bi bi-x"></i></button>
          </div>
          <p v-if="!settings.footer.links.length" class="small text-muted">No custom links. Add e.g. "Terms & Conditions" or "Returns policy".</p>
        </div>
      </div>

      <!-- SEO -->
      <div v-if="activeTab === 'seo'" class="row g-3">
        <div class="col-lg-6">
          <label class="form-label fw-bold">Page title</label>
          <input v-model="settings.seo.meta_title" type="text" class="form-control" maxlength="70" :placeholder="previewStoreName">
          <small class="text-muted">{{ (settings.seo.meta_title || '').length }}/70 characters</small>
          <label class="form-label fw-bold mt-3">Meta description</label>
          <textarea v-model="settings.seo.meta_description" class="form-control" rows="3" maxlength="170"></textarea>
          <small class="text-muted">{{ (settings.seo.meta_description || '').length }}/170 characters. Shown by search engines under your shop's name.</small>
        </div>
        <div class="col-lg-6">
          <div class="border rounded p-3 bg-white">
            <div class="small text-success">{{ shopUrl || 'https://your-shop' }}</div>
            <div class="text-primary fs-6">{{ settings.seo.meta_title || previewStoreName }}</div>
            <div class="small text-muted">{{ settings.seo.meta_description || 'Your meta description will appear here.' }}</div>
          </div>
        </div>
      </div>

      <div v-if="activeTab !== 'sections' && canEdit" class="settings-save-bar d-flex justify-content-end align-items-center gap-2 mt-4 pt-3 border-top">
        <span v-if="dirty" class="small text-warning me-auto"><i class="bi bi-circle-fill me-1"></i>You have unsaved changes</span>
        <button type="button" class="btn btn-outline-secondary" :disabled="!dirty || saving" @click="discardSettings">Discard</button>
        <button type="button" class="btn btn-primary" :disabled="!dirty || saving" @click="saveSettings">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save changes
        </button>
      </div>
    </template>

    <SectionEditor
      :open="editor.open"
      :section="editor.section"
      :initial-tab="editor.tab"
      :categories="categories"
      :brands="brands"
      :stores="stores"
      :section-defaults="options.section_defaults || {}"
      @closed="editor.open = false"
      @saved="onSectionSaved"
      @items-changed="refreshSections"
    />
  </Card>
</template>

<script>
import storefrontService from "@/api/storefrontService";
import categoryService from "@/api/categoryService";
import brandService from "@/api/brandService";
import permissions from "@/utils/permissions";
import confirmDialog from "@/utils/confirmDialog";
import { decrypt } from "@/utils/crypto";
import ThemePreview from "./components/ThemePreview.vue";
import ColorInput from "./components/ColorInput.vue";
import SectionEditor from "./components/SectionEditor.vue";
import { COLOR_GROUPS, PRODUCT_SOURCES, SECTION_TYPES, SOCIAL_NETWORKS, THEME_PRESETS } from "./storefrontMeta";

const DEFAULT_TITLES = {
  category_grid: 'Shop by Category',
  product_carousel: 'Featured Products',
  product_grid: 'Popular Products',
  brand_strip: 'Shop by Brand',
  banner_grid: 'Top Deals',
  text_block: 'Welcome to our store',
  newsletter: 'Get exclusive offers',
}

const STARTER_LAYOUT = [
  { type: 'hero_carousel', settings: { show_side_banners: true } },
  { type: 'features' },
  { type: 'category_grid', title: 'Shop by Category', subtitle: 'Browse our wide range of categories' },
  { type: 'product_carousel', title: 'New Arrivals', settings: { source: 'new_arrivals' } },
  { type: 'banner_grid', title: 'Top Deals', settings: { columns: 3 } },
  { type: 'product_carousel', title: 'Best Sellers', settings: { source: 'best_sellers' } },
  { type: 'brand_strip', title: 'Shop by Brand' },
  { type: 'product_grid', title: 'Trending Now', settings: { source: 'trending', limit: 10 } },
  { type: 'newsletter', title: 'Create an account for faster checkout', subtitle: 'Track your orders and save your delivery addresses.' },
]

export default {
  name: "Storefront",

  components: { ThemePreview, ColorInput, SectionEditor },

  data() {
    return {
      loading: true,
      saving: false,
      busy: false,
      activeTab: 'sections',
      tabs: [
        { key: 'sections', label: 'Sections & widgets', icon: 'bi-grid-1x2' },
        { key: 'theme', label: 'Theme & colors', icon: 'bi-palette' },
        { key: 'layout', label: 'Layout', icon: 'bi-layout-sidebar' },
        { key: 'header_footer', label: 'Header & footer', icon: 'bi-window-stack' },
        { key: 'seo', label: 'SEO', icon: 'bi-search' },
      ],
      settings: null,
      savedSnapshot: '',
      options: {},
      stores: [],
      sections: [],
      categories: [],
      brands: [],
      previewStoreId: null,
      profile: {},
      userPermissions: [],
      editor: { open: false, section: null, tab: 'content' },
      dragIndex: null,
      dragOverIndex: null,
      sectionTypes: SECTION_TYPES,
      colorGroups: COLOR_GROUPS,
      themePresets: THEME_PRESETS,
      socialNetworks: SOCIAL_NETWORKS,
      shopUrl: process.env.VUE_APP_SHOP_URL || '',
      layoutToggles: {
        sticky_header: 'Keep the header visible while scrolling',
        show_category_nav: 'Show the category menu bar under the header',
        show_category_sidebar: 'Show the category list on the left of the home page',
        show_brands_in_sidebar: 'Show brands in the left sidebar',
        show_all_products: 'Show an "All products" grid at the bottom of the home page',
      },
    }
  },

  computed: {
    canEdit() {
      if (['primary', 'super-admin'].includes(this.profile.role)) return true
      return permissions.hasPermission(this.userPermissions, 'edit storefront')
    },
    onlineStores() {
      return this.stores.filter(s => Number(s.online) === 1)
    },
    previewStoreName() {
      const store = this.stores.find(s => s.id === this.previewStoreId)
      return store ? (store.online_name || store.name) : 'Your Store'
    },
    previewUrl() {
      if (!this.shopUrl || !this.previewStoreId) return ''
      const base = this.shopUrl.endsWith('/') ? this.shopUrl : this.shopUrl + '/'
      return `${base}shop?store=${encodeURIComponent(this.previewStoreId)}&preview=1`
    },
    dirty() {
      return this.settings && JSON.stringify(this.settings) !== this.savedSnapshot
    },
  },

  methods: {
    sectionMeta(section) {
      return SECTION_TYPES[section.type] || { label: section.type, icon: 'bi-square' }
    },
    sectionSummary(section) {
      const s = section.settings || {}
      const items = (section.items || []).length
      switch (section.type) {
        case 'product_carousel':
        case 'product_grid': {
          let text = PRODUCT_SOURCES[s.source] || ''
          if (s.source === 'category') text += ': ' + (this.categories.find(c => c.id === s.category_id)?.name || 'not selected')
          if (s.source === 'brand') text += ': ' + (this.brands.find(b => b.id === s.brand_id)?.name || 'not selected')
          if (s.source === 'manual') text += ` (${(s.product_ids || []).length})`
          return text
        }
        case 'hero_carousel':
        case 'banner':
        case 'banner_grid':
          return `${items} image${items === 1 ? '' : 's'}`
        case 'features':
          return items ? `${items} items` : 'Default items'
        case 'category_grid':
          return s.source === 'manual' ? `${(s.category_ids || []).length} chosen categories` : 'All categories'
        case 'brand_strip':
          return s.source === 'manual' ? `${(s.brand_ids || []).length} chosen brands` : 'All brands'
        default:
          return ''
      }
    },
    needsItems(section) {
      return ['banner', 'banner_grid'].includes(section.type) && !(section.items || []).some(i => i.is_active)
    },

    async load() {
      this.loading = true
      try {
        const [storefront, sections, categories, brands] = await Promise.all([
          storefrontService.getStorefront(),
          storefrontService.getSections(),
          categoryService.getCategories({ per_page: 500 }),
          brandService.getBrands({ all: 1 }),
        ])
        if (storefront) {
          this.setSettings(storefront.settings)
          this.options = storefront.options || {}
          this.stores = storefront.stores || []
          this.previewStoreId = (this.onlineStores[0] || this.stores[0] || {}).id || null
        }
        this.sections = Array.isArray(sections) ? sections : []
        this.categories = categories?.data || []
        this.brands = Array.isArray(brands) ? brands : []
      } finally {
        this.loading = false
      }
    },
    setSettings(settings) {
      this.settings = JSON.parse(JSON.stringify(settings))
      if (!Array.isArray(this.settings.footer.links)) this.settings.footer.links = []
      if (!this.settings.footer.social || Array.isArray(this.settings.footer.social)) this.settings.footer.social = {}
      this.savedSnapshot = JSON.stringify(this.settings)
    },
    async refreshSections() {
      const sections = await storefrontService.getSections()
      this.sections = Array.isArray(sections) ? sections : []
      if (this.editor.open && this.editor.section) {
        this.editor.section = this.sections.find(s => s.id === this.editor.section.id) || this.editor.section
      }
    },

    // Settings
    applyPreset(preset) {
      this.settings.theme = { ...this.settings.theme, ...preset.theme }
    },
    discardSettings() {
      this.settings = JSON.parse(this.savedSnapshot)
    },
    async saveSettings() {
      const invalidLink = this.settings.footer.links.find(l => !l.label || !l.url)
      if (invalidLink) {
        this.$toast.error('Every footer link needs a label and a URL')
        return
      }
      this.saving = true
      try {
        const payload = JSON.parse(JSON.stringify(this.settings))
        Object.keys(payload.footer.social).forEach(k => { if (!payload.footer.social[k]) payload.footer.social[k] = null })
        const result = await storefrontService.updateSettings(payload)
        if (result) this.setSettings(result.settings)
      } finally {
        this.saving = false
      }
    },

    // Sections
    async addSection(type, extra = {}, openEditor = true) {
      this.busy = true
      try {
        const payload = {
          type,
          title: extra.title !== undefined ? extra.title : (DEFAULT_TITLES[type] || null),
          subtitle: extra.subtitle || null,
          is_active: true,
          settings: { ...(this.options.section_defaults?.[type] || {}), ...(extra.settings || {}) },
        }
        const section = await storefrontService.createSection(payload)
        if (section) {
          await this.refreshSections()
          if (openEditor) {
            this.openEditor(this.sections.find(s => s.id === section.id) || section, SECTION_TYPES[type]?.hasItems && type !== 'features' ? 'items' : 'content')
          }
        }
        return section
      } finally {
        this.busy = false
      }
    },
    async createStarterLayout() {
      this.busy = true
      try {
        for (const section of STARTER_LAYOUT) {
          await storefrontService.createSection({
            type: section.type,
            title: section.title || null,
            subtitle: section.subtitle || null,
            is_active: true,
            settings: { ...(this.options.section_defaults?.[section.type] || {}), ...(section.settings || {}) },
          })
        }
        await this.refreshSections()
      } finally {
        this.busy = false
      }
    },
    async duplicate(section) {
      const copy = await this.addSection(section.type, {
        title: section.title ? section.title + ' (copy)' : null,
        subtitle: section.subtitle,
        settings: section.settings,
      }, false)
      if (!copy) return
      for (const item of section.items || []) {
        await storefrontService.createItem(copy.id, {
          placement: item.placement, image: item.image, title: item.title, subtitle: item.subtitle,
          button_text: item.button_text, link_type: item.link_type || 'none', link_value: item.link_value,
          text_color: item.text_color, text_position: item.text_position, icon: item.icon, is_active: item.is_active,
        })
      }
      // Place the copy right after the original
      const order = this.sections.map(s => s.id).filter(id => id !== copy.id)
      order.splice(order.indexOf(section.id) + 1, 0, copy.id)
      await storefrontService.reorderSections(order)
      await this.refreshSections()
    },
    openEditor(section, tab = 'content') {
      this.editor = { open: true, section, tab }
    },
    async onSectionSaved() {
      await this.refreshSections()
    },
    async toggleSection(section) {
      const result = await storefrontService.updateSection(section.id, { is_active: !section.is_active }, null)
      if (result) section.is_active = result.is_active
    },
    removeSection(section) {
      confirmDialog('Section', async () => {
        if (await storefrontService.deleteSection(section.id)) await this.refreshSections()
      }, `Remove the "${section.title || this.sectionMeta(section).label}" section from your shop?`)
    },
    async saveOrder(list) {
      const previous = this.sections
      this.sections = list
      if (!(await storefrontService.reorderSections(list.map(s => s.id)))) this.sections = previous
    },
    move(index, delta) {
      const list = [...this.sections]
      const [moved] = list.splice(index, 1)
      list.splice(index + delta, 0, moved)
      this.saveOrder(list)
    },
    onDragStart(index, event) {
      this.dragIndex = index
      event.dataTransfer.effectAllowed = 'move'
    },
    onDrop(index) {
      if (this.dragIndex === null || this.dragIndex === index) return
      const list = [...this.sections]
      const [moved] = list.splice(this.dragIndex, 1)
      list.splice(index, 0, moved)
      this.dragIndex = null
      this.dragOverIndex = null
      this.saveOrder(list)
    },
  },

  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.userPermissions = await permissions.getUserPermissions(this.profile.id)
    await this.load()
  },
}
</script>

<style scoped>
.section-card {
  transition: box-shadow .15s ease, border-color .15s ease;
}

.section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, .06);
}

.section-card.drag-over {
  border-color: var(--bs-primary) !important;
  border-style: dashed !important;
}

.section-inactive {
  opacity: .55;
}

.section-icon {
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.drag-handle {
  cursor: grab;
}

.min-w-0 {
  min-width: 0;
}

.widget-library {
  top: 80px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.widget-option:hover {
  border-color: var(--bs-primary) !important;
  background: #fff;
}

.preview-sticky {
  top: 80px;
}

.preset-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.preset-swatches {
  display: inline-flex;
}

.preset-swatches span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, .1);
  margin-left: -3px;
}

.preset-swatches span:first-child {
  margin-left: 0;
}

.settings-save-bar {
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 5;
  padding-bottom: .5rem;
}
</style>
